import type { ReactNode } from 'react'

export type RecordState = 'draft' | 'submitted' | 'approved' | 'returned' | 'denied' | 'published' | 'amended'

/**
 * The record-state vocabulary from docs/design/style-guide.md: one label,
 * one icon, one semantic color role per state, used identically on chips,
 * table cells, calendar days, and the planning board. Draft is a solid
 * `surface-2` fill; every other state is its role color at a light tint,
 * so the chip never carries the state by color alone.
 */
const META: Record<RecordState, { label: string; className: string; icon: ReactNode }> = {
  draft: {
    label: 'Draft',
    className: 'bg-surface-2 text-ink-subtle',
    icon: (
      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    ),
  },
  submitted: {
    label: 'Submitted',
    className: 'bg-info/10 text-info',
    icon: <path d="M22 2 11 13M22 2 15 22l-4-9-9-4Z" />,
  },
  approved: {
    label: 'Approved',
    className: 'bg-success/10 text-success',
    icon: <path d="m20 6-11 11-5-5" />,
  },
  returned: {
    label: 'Returned',
    className: 'bg-warning/10 text-warning',
    icon: <path d="M9 14 4 9l5-5M4 9h10.5A5.5 5.5 0 0 1 20 14.5v0A5.5 5.5 0 0 1 14.5 20H11" />,
  },
  denied: {
    label: 'Denied',
    className: 'bg-error/10 text-error',
    icon: <path d="M18 6 6 18M6 6l12 12" />,
  },
  published: {
    label: 'Published',
    className: 'bg-primary/10 text-primary',
    icon: <path d="M4 3v18M4 4h12l-2 4 2 4H4" />,
  },
  amended: {
    label: 'Amended',
    className: 'bg-primary/10 text-primary ring-1 ring-inset ring-warning',
    icon: (
      <>
        <path d="M4 3v18M4 4h12l-2 4 2 4H4" />
        <circle cx="19" cy="5" r="3" className="fill-warning" stroke="none" />
      </>
    ),
  },
}

export default function StatusChip({ state }: { state: RecordState }) {
  const { label, className, icon } = META[state]
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-caption font-bold whitespace-nowrap ${className}`}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {icon}
      </svg>
      {label}
    </span>
  )
}
